import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../api';

const Dashboard = () => {
  const [stats, setStats] = useState({ 
    todaySales: 0, 
    totalCredit: 0, 
    monthPurchases: 0,
    todaySalesList: [],
    pendingCreditList: [],
    monthPurchasesList: []
  });
  const [loading, setLoading] = useState(true);
  
  // Search Modal States
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [selectedDetailRow, setSelectedDetailRow] = useState(null);
  const [allPurchases, setAllPurchases] = useState([]);
  const [allSales, setAllSales] = useState([]);

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      let statsData = null, purData = null, salesData = null;

      try {
        const [statsRes, purRes, salesRes] = await Promise.all([
          fetch(apiUrl('/api/dashboard-stats')),
          fetch(apiUrl('/api/purchases')),
          fetch(apiUrl('/api/sales'))
        ]);

        if (statsRes.ok) statsData = await statsRes.json();
        if (purRes.ok) purData = await purRes.json();
        if (salesRes.ok) salesData = await salesRes.json();
      } catch (e) {
        console.error('Error fetching dashboard endpoints:', e);
      }

      if (statsData) {
        setStats({
          todaySales: statsData.todaySales || 0,
          totalCredit: statsData.totalCredit || 0,
          monthPurchases: statsData.monthPurchases || 0,
          todaySalesList: statsData.todaySalesList || [],
          pendingCreditList: statsData.pendingCreditList || [],
          monthPurchasesList: statsData.monthPurchasesList || []
        });
      }
      if (purData) setAllPurchases(purData);
      if (salesData) setAllSales(salesData);
    } catch (error) {
      console.error('Failed to fetch dashboard stats & search data', error);
    } finally {
      setLoading(false);
    }
  };

  const [focusedCardIdx, setFocusedCardIdx] = useState(0);

  const cardRoutes = React.useMemo(() => [
    '/dashboard/today-sales',
    '/dashboard/pending-credit',
    '/dashboard/stockist-purchases',
    '/dashboard/monthly-purchases'
  ], []);

  useEffect(() => {
    fetchData();

    // Real-time background sync every 5 seconds for live sales & stock updates
    const interval = setInterval(() => {
      fetchData();
    }, 5000);

    const handleFocus = () => {
      fetchData();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Keyboard Right & Left Arrow Navigation across the 4 Dashboard summary cards
  useEffect(() => {
    const handleRightLeftKeys = (e) => {
      const activeElement = document.activeElement;
      if (activeElement) {
        const tagName = activeElement.tagName ? activeElement.tagName.toLowerCase() : '';
        if (tagName === 'input' || tagName === 'textarea' || tagName === 'select' || activeElement.isContentEditable) {
          return;
        }
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusedCardIdx(prev => (prev + 1) % cardRoutes.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusedCardIdx(prev => (prev - 1 + cardRoutes.length) % cardRoutes.length);
      } else if (e.key === 'Enter') {
        const targetPath = cardRoutes[focusedCardIdx];
        if (targetPath) {
          e.preventDefault();
          navigate(targetPath);
        }
      }
    };

    window.addEventListener('keydown', handleRightLeftKeys);
    return () => window.removeEventListener('keydown', handleRightLeftKeys);
  }, [focusedCardIdx, cardRoutes, navigate]);

  return (
    <div className="space-y-4 md:space-y-6 animate-in fade-in duration-700 p-2 md:p-4">
      
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 md:mb-4">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-6 md:h-8 rounded-full bg-blue-500 shadow-sm"></div>
            <h2 className="text-lg md:text-xl font-bold text-slate-800 tracking-wide">Dashboard Overview</h2>
          </div>
          <button 
            onClick={() => setShowSearchModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-[#1e5c5c] text-[#1e5c5c] rounded-xl hover:bg-[#1e5c5c] hover:text-white font-black text-xs md:text-sm shadow-sm transition-all"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            Search Product Distributors
          </button>
        </div>
        
        {/* Top 4 Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-6">
          
          {/* Today's Sales Card */}
          <div 
            className={`bg-gradient-to-b from-white via-slate-50/40 to-emerald-50/20 rounded-2xl p-5 border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 ease-out relative overflow-hidden group cursor-pointer flex flex-col justify-between ${
              focusedCardIdx === 0 
                ? 'border-emerald-500 ring-4 ring-emerald-400 scale-[1.03] shadow-2xl z-20' 
                : 'border-slate-200/90 hover:border-emerald-300 hover:-translate-y-1'
            }`}
            onClick={() => navigate('/dashboard/today-sales')}
            onMouseEnter={() => setFocusedCardIdx(0)}
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600 absolute top-0 left-0"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-400/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
            
            <div className="relative z-10 pt-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-[0_4px_14px_rgba(16,185,129,0.35)] group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-extrabold tracking-wider text-slate-400 block">Revenue</span>
                    <span className="text-sm font-extrabold text-slate-800 tracking-tight">Today's Sales</span>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-emerald-100/80 border border-emerald-200/90 px-2.5 py-1 text-[11px] font-black text-emerald-800 tracking-wide shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span>
                  LIVE
                </span>
              </div>
              
              <div>
                {loading ? (
                  <div className="h-10 w-32 bg-slate-200/60 animate-pulse rounded-lg mt-1"></div>
                ) : (
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-black text-emerald-600">₹</span>
                    <h3 className="text-3xl font-black text-slate-900 tracking-tight">{stats.todaySales}</h3>
                  </div>
                )}
              </div>
            </div>

            <div className="relative z-10 mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500 font-bold group-hover:text-emerald-700 transition-colors">
                View detailed report
              </p>
              <div className="w-7 h-7 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300 shadow-sm">
                <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Pending Credit Card */}
          <div 
            className={`bg-gradient-to-b from-white via-slate-50/40 to-rose-50/20 rounded-2xl p-5 border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 ease-out relative overflow-hidden group cursor-pointer flex flex-col justify-between ${
              focusedCardIdx === 1 
                ? 'border-rose-500 ring-4 ring-rose-400 scale-[1.03] shadow-2xl z-20' 
                : 'border-slate-200/90 hover:border-rose-300 hover:-translate-y-1'
            }`}
            onClick={() => navigate('/dashboard/pending-credit')}
            onMouseEnter={() => setFocusedCardIdx(1)}
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-rose-500 via-amber-400 to-rose-600 absolute top-0 left-0"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-rose-400/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
            
            <div className="relative z-10 pt-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500 to-amber-500 text-white flex items-center justify-center shadow-[0_4px_14px_rgba(244,63,94,0.35)] group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-extrabold tracking-wider text-slate-400 block">Dues</span>
                    <span className="text-sm font-extrabold text-slate-800 tracking-tight">Pending Credit</span>
                  </div>
                </div>
                <span className="rounded-full bg-amber-100/90 border border-amber-200/90 px-2.5 py-1 text-[11px] font-black text-amber-900 tracking-wide shadow-sm">
                  REVIEW
                </span>
              </div>
              
              <div>
                {loading ? (
                  <div className="h-10 w-32 bg-slate-200/60 animate-pulse rounded-lg mt-1"></div>
                ) : (
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-black text-rose-600">₹</span>
                    <h3 className="text-3xl font-black text-slate-900 tracking-tight">{stats.totalCredit}</h3>
                  </div>
                )}
              </div>
            </div>

            <div className="relative z-10 mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500 font-bold group-hover:text-rose-700 transition-colors">
                View detailed report
              </p>
              <div className="w-7 h-7 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center group-hover:bg-rose-500 group-hover:text-white transition-all duration-300 shadow-sm">
                <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Stockist Purchases & Bills Card */}
          <div 
            className={`bg-gradient-to-b from-white via-slate-50/40 to-indigo-50/20 rounded-2xl p-5 border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 ease-out relative overflow-hidden group cursor-pointer flex flex-col justify-between ${
              focusedCardIdx === 2 
                ? 'border-indigo-500 ring-4 ring-indigo-400 scale-[1.03] shadow-2xl z-20' 
                : 'border-slate-200/90 hover:border-indigo-300 hover:-translate-y-1'
            }`}
            onClick={() => navigate('/dashboard/stockist-purchases')}
            onMouseEnter={() => setFocusedCardIdx(2)}
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-purple-400 to-indigo-600 absolute top-0 left-0"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-400/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
            
            <div className="relative z-10 pt-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-[0_4px_14px_rgba(99,102,241,0.35)] group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-extrabold tracking-wider text-slate-400 block">Inward</span>
                    <span className="text-sm font-extrabold text-slate-800 tracking-tight">Stockist Purchases</span>
                  </div>
                </div>
                <span className="rounded-full bg-indigo-100/90 border border-indigo-200/90 px-2.5 py-1 text-[11px] font-black text-indigo-900 tracking-wide shadow-sm">
                  {allPurchases.length} BILLS
                </span>
              </div>
              
              <div>
                {loading ? (
                  <div className="h-10 w-32 bg-slate-200/60 animate-pulse rounded-lg mt-1"></div>
                ) : (
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-black text-indigo-600">₹</span>
                    <h3 className="text-3xl font-black text-slate-900 tracking-tight">
                      {allPurchases.reduce((acc, p) => acc + (parseFloat(p.netAmount || p.totalAmount || p.grossAmount || 0) || 0), 0).toFixed(2)}
                    </h3>
                  </div>
                )}
              </div>
            </div>

            <div className="relative z-10 mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500 font-bold group-hover:text-indigo-700 transition-colors">
                View all stockist bills
              </p>
              <div className="w-7 h-7 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-500 group-hover:text-white transition-all duration-300 shadow-sm">
                <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Credit Purchases Card */}
          <div 
            className={`bg-gradient-to-b from-white via-slate-50/40 to-sky-50/20 rounded-2xl p-5 border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 ease-out relative overflow-hidden group cursor-pointer flex flex-col justify-between ${
              focusedCardIdx === 3 
                ? 'border-sky-500 ring-4 ring-sky-400 scale-[1.03] shadow-2xl z-20' 
                : 'border-slate-200/90 hover:border-sky-300 hover:-translate-y-1'
            }`}
            onClick={() => navigate('/dashboard/monthly-purchases')}
            onMouseEnter={() => setFocusedCardIdx(3)}
          >
            <div className="h-1.5 w-full bg-gradient-to-r from-sky-500 via-blue-500 to-cyan-400 absolute top-0 left-0"></div>
            <div className="absolute top-0 right-0 w-32 h-32 bg-sky-400/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:scale-125 transition-transform duration-500 pointer-events-none"></div>
            
            <div className="relative z-10 pt-1">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center shadow-[0_4px_14px_rgba(14,165,233,0.35)] group-hover:scale-105 group-hover:rotate-3 transition-all duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
                    </svg>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-extrabold tracking-wider text-slate-400 block">Monthly</span>
                    <span className="text-sm font-extrabold text-slate-800 tracking-tight">Credit Purchases</span>
                  </div>
                </div>
                <span className="rounded-full bg-sky-100/90 border border-sky-200/90 px-2.5 py-1 text-[11px] font-black text-sky-900 tracking-wide shadow-sm">
                  BUDGET
                </span>
              </div>
              
              <div>
                {loading ? (
                  <div className="h-10 w-32 bg-slate-200/60 animate-pulse rounded-lg mt-1"></div>
                ) : (
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-black text-sky-600">₹</span>
                    <h3 className="text-3xl font-black text-slate-900 tracking-tight">{stats.monthPurchases}</h3>
                  </div>
                )}
              </div>
            </div>

            <div className="relative z-10 mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500 font-bold group-hover:text-sky-700 transition-colors">
                View detailed report
              </p>
              <div className="w-7 h-7 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center group-hover:bg-sky-500 group-hover:text-white transition-all duration-300 shadow-sm">
                <svg className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search History Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-5xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
             <div className="bg-[#1e5c5c] text-white p-4 flex justify-between items-center shrink-0">
               <h3 className="font-bold text-lg flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                Search Product Distributors
              </h3>
               <button onClick={() => { setShowSearchModal(false); setGlobalSearchQuery(''); }} className="text-white/70 hover:text-white transition-colors">
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
               </button>
             </div>
             <div className="p-4 border-b border-slate-200 shrink-0 bg-slate-50">
               <input 
                 type="text" 
                 autoFocus
                 value={globalSearchQuery}
                 onChange={e => setGlobalSearchQuery(e.target.value)}
                 placeholder="Search by Product Name or Party (Supplier) Name..."
                 className="w-full p-3 border-2 border-slate-300 rounded-xl focus:border-[#1e5c5c] outline-none font-bold text-slate-700 text-sm shadow-sm"
               />
             </div>
             <div className="flex-1 overflow-auto bg-slate-100 p-4">
                 {(() => {
                  const getProductStats = (productName, packing = '') => {
                    if (!productName) return { bought: 0, sold: 0, stock: 0 };
                    const targetClean = productName.trim().toUpperCase().replace(/\s+/g, ' ');
                    const packTarget = (packing || '').toString().trim().toUpperCase();
                    let totalPurchased = 0;
                    let totalSold = 0;

                    (allPurchases || []).forEach(b => (b.items || []).forEach(i => {
                      const pName = (i.productName || i.name || '').trim().toUpperCase().replace(/\s+/g, ' ');
                      const pPack = (i.packing || i.package || i.pack || i.packSize || i.pkg || i.strip || '').toString().trim().toUpperCase();
                      if (pName === targetClean && (!packTarget || !pPack || pPack === packTarget)) {
                        const billedQty = parseFloat(i.qty) || 0;
                        const freeQty = parseFloat(i.freeQty || i.free || i.sch || i.scheme || 0) || 0;
                        totalPurchased += (billedQty + freeQty);
                      }
                    }));

                    (allSales || []).forEach(b => (b.items || []).forEach(i => {
                      const pName = (i.productName || i.name || i.item || '').trim().toUpperCase().replace(/\s+/g, ' ');
                      const pPack = (i.packing || i.package || i.pack || i.packSize || i.pkg || i.strip || '').toString().trim().toUpperCase();
                      if (pName === targetClean && (!packTarget || !pPack || pPack === packTarget)) {
                        totalSold += parseFloat(i.qty) || 0;
                      }
                    }));

                    const stock = Math.max(0, totalPurchased - totalSold);
                    return { bought: totalPurchased, sold: totalSold, stock };
                  };

                  const results = globalSearchQuery.trim() === '' ? [] : allPurchases.flatMap(bill => {
                    return (bill.items || []).filter(item => 
                      item.productName?.toLowerCase().includes(globalSearchQuery.toLowerCase()) || 
                      (item.packing && item.packing.toLowerCase().includes(globalSearchQuery.toLowerCase())) ||
                      bill.supplierName?.toLowerCase().includes(globalSearchQuery.toLowerCase())
                    ).map(item => ({
                      ...item,
                      billDate: bill.date,
                      supplierName: bill.supplierName,
                      stats: getProductStats(item.productName, item.packing || item.package || item.pack)
                    }));
                  }).sort((a, b) => new Date(b.billDate) - new Date(a.billDate));

                  if (results.length > 0) {
                    return (
                      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                              <th className="p-3 border-b border-slate-200">Date</th>
                              <th className="p-3 border-b border-slate-200">Product</th>
                              <th className="p-3 border-b border-slate-200">Party (Supplier)</th>
                              <th className="p-3 border-b border-slate-200 text-center">Bought (This Bill)</th>
                              <th className="p-3 border-b border-slate-200 text-center">Total Sold</th>
                              <th className="p-3 border-b border-slate-200 text-center">Stock</th>
                              <th className="p-3 border-b border-slate-200 text-right">Stock Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {results.map((res, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedDetailRow(res)}>
                                <td className="p-3 text-xs text-slate-600 font-medium whitespace-nowrap">{new Date(res.billDate).toLocaleDateString('en-GB')}</td>
                                <td className="p-3 text-xs text-slate-800 font-bold uppercase">
                                  <div>{res.productName}</div>
                                  {(res.packing || res.package || res.pack) && (
                                    <span className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.2 rounded font-black inline-block mt-0.5">
                                      Pkg: {res.packing || res.package || res.pack}
                                    </span>
                                  )}
                                </td>
                                <td className="p-3 text-xs text-slate-600 font-bold uppercase">{res.supplierName}</td>
                                <td className="p-3 text-xs text-center font-bold text-blue-600">{res.qty}</td>
                                <td className="p-3 text-xs text-center font-bold text-rose-600">{res.stats.sold}</td>
                                <td className="p-3 text-xs text-center">
                                  <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-bold">
                                    {res.stats.stock}
                                  </span>
                                </td>
                                <td className="p-3 text-xs text-slate-700 font-bold text-right">₹{((parseFloat(res.rate)||0) * (res.stats.stock > 0 ? res.stats.stock : 0)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                  
                  if (globalSearchQuery.trim() !== '') {
                    return (
                      <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                        <svg className="w-12 h-12 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                        <p className="font-bold text-sm">No results found for "{globalSearchQuery}"</p>
                      </div>
                    );
                  }

                  return (
                    <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                      <svg className="w-12 h-12 mb-2 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                      <p className="font-bold text-sm">Type a product or supplier name to view distributor records & stock</p>
                    </div>
                  );
                })()}
             </div>
          </div>
        </div>
      )}

      {selectedDetailRow && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-teal-700 text-white p-4 flex justify-between items-center shrink-0">
               <h3 className="font-bold text-lg">Product Details</h3>
               <button onClick={() => setSelectedDetailRow(null)} className="text-white/70 hover:text-white transition-colors">
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
               </button>
            </div>
            <div className="p-6 space-y-4">
               <div>
                 <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Product</p>
                 <p className="text-lg font-black text-slate-800 uppercase">{selectedDetailRow.productName}</p>
               </div>
               <div>
                 <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Party (Supplier)</p>
                 <p className="text-base font-bold text-teal-700 uppercase">{selectedDetailRow.supplierName}</p>
               </div>
               <div className="grid grid-cols-2 gap-4">
                 <div className="bg-blue-50 p-3 rounded-xl border border-blue-100">
                   <p className="text-xs text-blue-500 font-bold uppercase tracking-wider mb-1">Bought (This Bill)</p>
                   <p className="text-xl font-black text-blue-700">{selectedDetailRow.qty}</p>
                 </div>
                 <div className="bg-rose-50 p-3 rounded-xl border border-rose-100">
                   <p className="text-xs text-rose-500 font-bold uppercase tracking-wider mb-1">Total Sold</p>
                   <p className="text-xl font-black text-rose-700">{selectedDetailRow.stats.sold}</p>
                 </div>
               </div>
               <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100 flex justify-between items-center">
                 <div>
                   <p className="text-xs text-emerald-600 font-bold uppercase tracking-wider mb-1">Current Stock</p>
                   <p className="text-2xl font-black text-emerald-700">{selectedDetailRow.stats.stock}</p>
                 </div>
                 <div className="text-right">
                   <p className="text-xs text-emerald-600 font-bold uppercase tracking-wider mb-1">Stock Amount</p>
                   <p className="text-xl font-black text-emerald-700">₹{((parseFloat(selectedDetailRow.rate)||0) * (selectedDetailRow.stats.stock > 0 ? selectedDetailRow.stats.stock : 0)).toFixed(2)}</p>
                 </div>
               </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
